import { PageTitleNavs } from "@/common/components/page-title-navs";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  dummyDistributors,
  getActiveCartonData,
  getCartonData,
  getProductData,
  recallReasonInputs,
} from "../data";
import { ViewRecallWrapper } from "../styles";
import { Button } from "@/common/components/button";
import { Modal } from "@/common/components/modal";
import { useState } from "react";
import { Prompt } from "@/common/components/prompt";
import RecallProductDetails from "../sub/product-details";
import CartonPreview from "../sub/carton-preview";
import CartonDetails from "../sub/carton-details";
import DistributorDetails from "../sub/distributor-details";
import useQuery from "@/hooks/useQuery";
import { PRODUCT_RECALL, RECALL_RESPONSE } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useAuthDetails } from "@/hooks/useAuthDetails";

enum IAction {
  DISPATCH = "dispatch",
  RECIEVE = "recieve",
  INITIATE = "initiate",
}

const RECALL_ENDPOINT_CONFIG: Record<string, (productId: string) => string> = {
  manufacturer: (id) => `${PRODUCT_RECALL}/${id}`,
  distributor: (id) => `${RECALL_RESPONSE}/${id}`,
  retailer: (id) => `${RECALL_RESPONSE}/${id}`,
};

function getRecallRoute(
  role: string,
  action: IAction,
  id: string,
): string | null {
  const routes: Record<
    string,
    Partial<Record<IAction, string>> & { default?: string }
  > = {
    manufacturer: {
      default: `/recall/scan/${id}`,
      [IAction.RECIEVE]: `/deliveries/scan-receive/${id}`,
    },
    distributor: {
      [IAction.DISPATCH]: `/deliveries/auto-delivery-request/${id}`,
      [IAction.RECIEVE]: `/deliveries/scan-receive/${id}`,
    },
    retailer: { default: `/deliveries/auto-delivery-request/${id}` },
  };

  const roleRoutes = routes[role];
  if (!roleRoutes) return null;
  return roleRoutes[action] ?? roleRoutes.default ?? null;
}

const ViewRecall = () => {
  const [viewCarton, setViewCarton] = useState<boolean>(false);
  const [viewConfirm, setViewConfirm] = useState<boolean>(false);
  const [activeSerial, setActiveSelection] = useState<string | null>(null);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const action = searchParams.get("action") as IAction;
  const batch_id = searchParams.get("batch");
  const id = useParams().id;
  const makeRequest = useApiCallHandling();

  const [loadingRecall, setLoadingRecall] = useState(false);
  const { companyType } = useAuthDetails();
  const role = companyType?.toLowerCase();
  const recallEndpoint = role && id ? RECALL_ENDPOINT_CONFIG[role]?.(id) : null;
  const { data: recallDetails } = useQuery(recallEndpoint as string);
  const recallData = Array.isArray(recallDetails)
    ? recallDetails[0]
    : Array.isArray(recallDetails?.recalled_products)
      ? recallDetails
      : recallDetails?.recalled_product;

  const handleOpenModal = (serial: string) => {
    setActiveSelection(serial);
    setViewCarton(true);
  };

  const handleConfirm = () => {
    setViewConfirm(true);
  };

  const handleScan = () => {
    if (!role) return;
    const path = getRecallRoute(role, action, id as string);
    if (!path) return;
    navigate(`/dashboard${path}?from=recall`);
  };

  const handleRecall = (data: any) => {
    setLoadingRecall(true);
    const payload = {
      ...data,
      affected_batch: {
        product_id: id,
        batch_number: batch_id,
      },
    };

    makeRequest(
      PRODUCT_RECALL,
      "POST",
      payload,
      () => {
        navigate("/dashboard/recall");
      },
      console.log,
      () => setLoadingRecall(false),
    );
  };

  const showDistributors =
    action !== IAction.DISPATCH && (recallData?.distributors?.length ?? 0) > 0;

  const actionConfig = {
    [IAction.DISPATCH]: {
      label: "Click to Scan and DIspatch",
      handler: handleScan,
    },
    [IAction.RECIEVE]: { label: "Scan to Recieve", handler: handleScan },
    [IAction.INITIATE]: { label: "Initiate Recall", handler: handleConfirm },
  };

  const currentAction = actionConfig[action];

  return (
    <ViewRecallWrapper>
      <PageTitleNavs
        title="Recall Management"
        backLink="/dashboard/recall"
        bcrumbs={[{ title: "Recall", link: "/dashboard/recall" }]}
      />
      <div className="containerWrapper">
        <RecallProductDetails data={getProductData(recallData)} />
        <CartonDetails
          data={getCartonData(recallData?.carton_units)}
          handler={handleOpenModal}
        />
        {showDistributors && <DistributorDetails data={dummyDistributors} />}
        <div className="submitWrapper">
          <Button onClick={currentAction?.handler}>
            {currentAction?.label}
          </Button>
        </div>
      </div>
      <Modal
        isOpen={viewCarton}
        close={() => setViewCarton(false)}
        title="Carton Details"
      >
        <CartonPreview
          data={getActiveCartonData(activeSerial as string, recallData)}
        />
      </Modal>
      <Modal isOpen={viewConfirm} close={() => setViewConfirm(false)}>
        <div className="flex items-center justify-center flex-col bg-white modal max-w-[800px] md:w-[600px]">
          <Prompt
            type="question"
            prompt="Are you sure you want to recall this product?
          This action can not be reversed."
            onMainAction={handleRecall}
            onSecondaryAction={() => console.log("secondary")}
            withInput
            formInput={recallReasonInputs}
            mainLoading={loadingRecall}
          />
        </div>
      </Modal>
    </ViewRecallWrapper>
  );
};

export default ViewRecall;
